import React, { useState } from 'react';
import { Reminder } from '../models/reminder';
import ConfirmModal from './ConfirmModal';

interface ReminderCardProps {
  reminder: Reminder;
  patientId: string;
  onToggleEnabled: (reminder: Reminder) => Promise<void>;
  onEdit: (reminder: Reminder) => void;
  onDelete: (reminder: Reminder) => void;
}

const DAY_LABELS: Record<string, string> = {
  Monday: 'Lunes',
  Tuesday: 'Martes',
  Wednesday: 'Miércoles',
  Thursday: 'Jueves',
  Friday: 'Viernes',
  Saturday: 'Sábado',
  Sunday: 'Domingo',
  'all-week': 'Todos los días',
  'working-days': 'Días laborables',
  weekend: 'Fin de semana',
};


const ReminderCard: React.FC<ReminderCardProps> = ({ reminder, onToggleEnabled, onEdit, onDelete }) => {
  const [toggling, setToggling] = useState(false);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const handleToggle = async () => {
    setToggling(true);
    try {
      await onToggleEnabled(reminder);
    } finally {
      setToggling(false);
    }
  };

  const handleDeleteClick = () => {
    if (reminder.scope_type === 'system') {
      setDisableConfirmOpen(true);
    } else {
      setDeleteConfirmOpen(true);
    }
  };

  const handleDisableInstead = async () => {
    setDisableConfirmOpen(false);
    if (reminder.enabled) {
      setToggling(true);
      try {
        await onToggleEnabled(reminder);
      } finally {
        setToggling(false);
      }
    }
  };

  const handleProceedToDelete = () => {
    setDisableConfirmOpen(false);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = () => {
    setDeleteConfirmOpen(false);
    onDelete(reminder);
  };

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: 10,
      padding: '16px 20px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: 16,
    }}>
      {/* Left: toggle */}
      <div style={{ paddingTop: 2 }}>
        <button
          onClick={handleToggle}
          disabled={toggling}
          title={reminder.enabled ? 'Desactivar' : 'Activar'}
          style={{
            width: 40,
            height: 22,
            borderRadius: 11,
            border: 'none',
            background: reminder.enabled ? '#38b2ac' : '#d1d5db',
            cursor: toggling ? 'not-allowed' : 'pointer',
            position: 'relative',
            transition: 'background 0.2s',
            flexShrink: 0,
          }}
        >
          <span style={{
            position: 'absolute',
            top: 3,
            left: reminder.enabled ? 21 : 3,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: '#fff',
            transition: 'left 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
          }} />
        </button>
      </div>

      {/* Center: content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#1f2937' }}>{reminder.title}</span>
          {/* Scope badge */}
          <span style={{
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 999,
            background: reminder.scope_type === 'system' ? '#e6fffa' : '#ede9fe',
            color: reminder.scope_type === 'system' ? '#38b2ac' : '#7c3aed',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}>
            {reminder.scope_type === 'system' ? 'Sistema' : 'Usuario'}
          </span>
        </div>

        {reminder.description && (
          <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6 }}>{reminder.description}</div>
        )}

        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#374151' }}>
            {reminder.notification_type === 'push' ? '📲 Push' : '📧 Email'}
          </span>
          {reminder.day_of_week && (
            <span style={{ fontSize: 13, color: '#374151' }}>
              📅 {DAY_LABELS[reminder.day_of_week] ?? reminder.day_of_week}
            </span>
          )}
          {reminder.date && (
            <span style={{ fontSize: 13, color: '#374151' }}>
              📅 {new Date(reminder.date).toLocaleDateString('es-ES')}
            </span>
          )}
          <span style={{ fontSize: 13, color: '#374151' }}>🕐 {reminder.time}</span>
          {reminder.repeatable && (
            <span style={{ fontSize: 13, color: '#374151' }}>🔁 Repetible</span>
          )}
          {reminder.last_sent_at ? (
            <span style={{ fontSize: 13, color: '#6b7280' }}>
              Último envío: {new Date(reminder.last_sent_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
            </span>
          ) : (
            <span style={{ fontSize: 13, color: '#9ca3af' }}>Sin envíos aún</span>
          )}
        </div>
      </div>

      {/* Right: actions */}
      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
        <button
          onClick={() => onEdit(reminder)}
          style={{
            background: '#38b2ac',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '6px 14px',
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#319795'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#38b2ac'; }}
        >
          Editar
        </button>
        <button
          onClick={handleDeleteClick}
          style={{
            background: 'transparent',
            color: '#e53e3e',
            border: '1px solid #e53e3e',
            borderRadius: 6,
            padding: '6px 14px',
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#fff5f5'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          Eliminar
        </button>
      </div>

      {/* Step 1 for system reminders: suggest disabling first */}
      <ConfirmModal
        open={disableConfirmOpen}
        title="¿Desactivar en lugar de eliminar?"
        message={
          <>
            Los recordatorios del sistema no se pueden recuperar una vez eliminados.
            <br /><br />
            <strong>¿Prefieres desactivarlo</strong> en lugar de eliminarlo permanentemente?
          </>
        }
        confirmText="Desactivar"
        cancelText="Continuar con eliminación"
        onConfirm={handleDisableInstead}
        onCancel={handleProceedToDelete}
      />

      {/* Final delete confirmation */}
      <ConfirmModal
        open={deleteConfirmOpen}
        title="Confirmar eliminación"
        message={
          <>
            Esta acción <strong>no se puede deshacer</strong>. El recordatorio <strong>"{reminder.title}"</strong> será eliminado permanentemente.
            <br /><br />
            ¿Confirmas la eliminación?
          </>
        }
        confirmText="Sí, Eliminar"
        cancelText="Cancelar"
        confirmButtonColor="#e53e3e"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </div>
  );
};

export default ReminderCard;
